import {
  address_verification_check,
  address_verification_check_large,
  adverse_media_check,
  adverse_media_check_large,
  entity_adverse_media_check,
  entity_adverse_media_check_large,
  ln_screening_check,
  ln_screening_check_entity,
  ln_screening_check_entity_large,
  ln_screening_check_large,
  pep_sanctions_check,
  pep_sanctions_check_large,
  sanctions_check,
  sanctions_check_large,
} from "../assets/checkpoints";

export type ScreeningProvider = "LexisNexis" | "LexisNexis2" | "WorldCheck";

/**
 * The screening check names each provider generation offers, by subject type.
 *
 * These are the raw check names as stored on MandateCheck. Anything passed to a
 * `name__in` query string by manual concatenation needs the ampersand encoded
 * first - see SCREENING_CHECK_QUERY_NAMES below.
 */
export const SCREENING_CHECK_NAMES = {
  LexisNexis: {
    individual: ["pep_&_sanctions_check", "adverse_media_check"],
    entity: ["sanctions_check", "entity_adverse_media_check"],
  },
  LexisNexis2: {
    individual: ["ln_screening_check"],
    entity: ["ln_screening_check_entity"],
  },
};

export const ADDRESS_CHECK_NAMES: Record<ScreeningSubject, string[]> = {
  individual: ["address_verification_check"],
  entity: [],
};

export type ScreeningSubject = "individual" | "entity";

/**
 * Every screening check name across both generations, for a given subject type.
 *
 * `GET individual/mandate_check/` filters the catalogue by the tenant's own
 * screening provider, so a wizard can ask for the union and receive only the
 * checks that tenant may actually select. That keeps one code path for both
 * generations and leaves the fail-safe direction with the server.
 */
export const allScreeningCheckNames = (subject: ScreeningSubject) => [
  ...SCREENING_CHECK_NAMES.LexisNexis[subject],
  ...SCREENING_CHECK_NAMES.LexisNexis2[subject],
];

/** As above, with the ampersand encoded for a hand-built `name__in` query string. */
export const SCREENING_CHECK_QUERY_NAMES: Record<ScreeningSubject, string[]> = {
  individual: allScreeningCheckNames("individual").map((name) =>
    name.replace("&", "%26")
  ),
  entity: allScreeningCheckNames("entity"),
};

/**
 * The full `name__in` list a screening wizard asks for, by subject type.
 *
 * Address verification is provider agnostic and is offered alongside the
 * screening checks for individuals. The server returns only the checks the
 * tenant's own provider allows, so the wizard needs no provider branch.
 */
export const screeningWizardCheckNames = (subject: ScreeningSubject) => [
  ...ADDRESS_CHECK_NAMES[subject],
  ...SCREENING_CHECK_QUERY_NAMES[subject],
];

/**
 * Icon per check name, for every screening surface that renders a check card.
 *
 * The two ln_* names alias the legacy assets until a dedicated icon exists;
 * `assets/checkpoints/index.ts` already aliases one asset to several names.
 */
export const CHECK_ICONS: Record<string, any> = {
  address_verification_check: address_verification_check,
  adverse_media_check: adverse_media_check,
  entity_adverse_media_check: entity_adverse_media_check,
  sanctions_check: sanctions_check,
  "pep_&_sanctions_check": pep_sanctions_check,
  // the encoded form reaches some surfaces through router state
  "pep_%26_sanctions_check": pep_sanctions_check,
  ln_screening_check: ln_screening_check,
  ln_screening_check_entity: ln_screening_check_entity,
};

/**
 * What a selection of checks actually covers.
 *
 * A single LexisNexis Screening check returns PEP, sanctions and adverse media
 * in one payload, so it satisfies each of these where the legacy generation
 * needed a separate check for each. The wizards carry these as router state.
 */
export const selectionCoversPepAndSanctions = (names: string[]) =>
  names.some((name) =>
    [
      "pep_&_sanctions_check",
      "pep_%26_sanctions_check",
      "ln_screening_check",
    ].includes(name)
  );

export const selectionCoversSanctions = (names: string[]) =>
  names.some((name) =>
    ["sanctions_check", "ln_screening_check_entity"].includes(name)
  );

export const selectionCoversAdverseMedia = (names: string[]) =>
  names.some((name) =>
    [
      "adverse_media_check",
      "entity_adverse_media_check",
      "ln_screening_check",
      "ln_screening_check_entity",
    ].includes(name)
  );

/**
 * The monitoring events to list back to the user on a wizard summary step.
 *
 * Under LexisNexis2 there is one event, because one check covers PEP, sanctions
 * and adverse media. Under the legacy provider it is whichever of the two the
 * user selected. Values arrive as either booleans or their string forms
 * depending on which wizard set them.
 */
export const monitoringEventSummary = ({
  pepRequired,
  adverseRequired,
  isEntity,
  isLexisNexis2,
}: {
  pepRequired: any;
  adverseRequired: any;
  isEntity: boolean;
  isLexisNexis2: boolean;
}) => {
  const isYes = (value: any) => value === true || value === "true";

  if (isLexisNexis2) {
    return [
      {
        key: "screening",
        label: "Screening",
        icon: CHECK_ICONS[
          isEntity ? "ln_screening_check_entity" : "ln_screening_check"
        ],
      },
    ];
  }

  const events = [];

  if (isYes(pepRequired)) {
    events.push({
      key: "pep",
      label: isEntity ? "Sanctions" : "PEP & Sanctions",
      icon: CHECK_ICONS[isEntity ? "sanctions_check" : "pep_&_sanctions_check"],
    });
  }

  if (isYes(adverseRequired)) {
    events.push({
      key: "adverse",
      label: "Adverse Media",
      icon: CHECK_ICONS[
        isEntity ? "entity_adverse_media_check" : "adverse_media_check"
      ],
    });
  }

  return events;
};

/** As CHECK_ICONS, for the surfaces that render the large variant. */
export const CHECK_ICONS_LARGE: Record<string, any> = {
  address_verification_check: address_verification_check_large,
  adverse_media_check: adverse_media_check_large,
  entity_adverse_media_check: entity_adverse_media_check_large,
  sanctions_check: sanctions_check_large,
  "pep_&_sanctions_check": pep_sanctions_check_large,
  "pep_%26_sanctions_check": pep_sanctions_check_large,
  ln_screening_check: ln_screening_check_large,
  ln_screening_check_entity: ln_screening_check_entity_large,
};
